import * as React from 'react'
import styled from 'styled-components'
import { useSpring, animated } from 'react-spring'
import { ApolloQueryResult } from '@apollo/client'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import dynamic from 'next/dynamic'
// import { useMediaQuery } from '@material-ui/core'
// import dynamic from 'next/dynamic'
// import Slogan from '../components/global/Slogan'
// import { Glow } from '../components/global/Glow'

import { Query_Root } from '../graphql/indexer-graphql-types'
import { FIRST_VIDEO_QUERY, indexerClient } from '../graphql/graphql-queries'
import { VideoFullData } from '../interfaces'
import { getMarketplaceData } from '../data/IndexerUtils'
import { fbEnrichUsersFromIds, fbEnrichVideoFromId } from '../data/FirebaseEnrich'
import { HomeVideosType } from '../components/home/HomeVideos'
import SkeletonFirstVideo from '../components/home/SkeletonFirstVideo'
import Leaderboard, { LeaderboardPageType } from '../components/leaderboard'
import FirstVideoWrapper from '../components/home/FirstVideoWrapper'
import { SubmitVideoButton } from '../components/upload/common/SubmitVideoButton'
import GlobalyActivityFeedSection from '../src/features/global-activity-feed/presentation/GlobalActivityFeedSection'

const FirstVideo = dynamic(() => import('../components/home/FirstVideo'), {
  loading: () => <SkeletonFirstVideo />,
})
const HomeVideos = dynamic(() => import('../components/home/HomeVideos'))
const Layout = dynamic(() => import('../components/global/Layout'))

//* ============================= Types ========================================

type IndexPageProps = {
  initialVideo?: VideoFullData
}

const AmplitudeInit = dynamic(() => import('../components/global/AmplitudeInit'), {
  ssr: false,
})

//* ============================= React Component ==============================

const IndexPage = ({ initialVideo }: IndexPageProps): JSX.Element => {
  const [card1Hover, setCard1Hover] = React.useState(false)
  const [card2Hover, setCard2Hover] = React.useState(false)
  const [card3Hover, setCard3Hover] = React.useState(false)
  // const isMobile = useMediaQuery('(max-width:62em)')
  const card1HoverState = useSpring({
    boxShadow: card1Hover ? '0px 5px 25px rgba(255,255,255,0.1)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: card1Hover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })
  const card2HoverState = useSpring({
    boxShadow: card2Hover ? '0px 5px 25px rgba(255,255,255,0.1)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: card2Hover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })
  const card3HoverState = useSpring({
    boxShadow: card3Hover ? '0px 5px 25px rgba(255,255,255,0.1)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: card3Hover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  return (
    <Layout title="Glass">
      <AmplitudeInit page="Home" />
      <ContentDiv>
        {/* {!isMobile && <Glow />} */}
        {/* <Slogan /> */}
        <FirstVideoWrapper>
          {initialVideo ? <FirstVideo initialVideo={initialVideo} /> : <SkeletonFirstVideo />}
        </FirstVideoWrapper>
        <Leaderboard type={LeaderboardPageType.HOME} />
        <GlobalyActivityFeedSection />
        <HomeVideos type={HomeVideosType.UPCOMING} />
        {/* <HomeVideos type={HomeVideosType.LATEST} /> */}
        <SecondLandingDiv id="learn-section">
          <StyledImg src={`https://glassimg.imgix.net/new-icon.png?fm=webp&w=750&h=750`} />
          <ActionsDiv>
            <LandingTitle>{`Glass is where videos\nare valued for what\nthey're worth.`}</LandingTitle>
          </ActionsDiv>
        </SecondLandingDiv>
        <CardsDiv>
          <Card
            onMouseEnter={() => setCard1Hover(true)}
            onMouseLeave={() => setCard1Hover(false)}
            style={card1HoverState}
            onClick={() =>
              window.open('https://glass.helpkit.so/create/8yiJdScMcaYEq12nP3HGst', '_blank')
            }
          >
            <CardNumber>{`1`}</CardNumber>
            <CardTitle>Create</CardTitle>
            <CardDescription>Monetize your videos better with video NFTs.</CardDescription>
          </Card>
          <Card
            onMouseEnter={() => setCard2Hover(true)}
            onMouseLeave={() => setCard2Hover(false)}
            style={card2HoverState}
            onClick={() =>
              window.open('https://glass.helpkit.so/collect/vNKyfMrK5cZ1hE6XaV3BGG', '_blank')
            }
          >
            <CardNumber>{`2`}</CardNumber>
            <CardTitle>Collect</CardTitle>
            <CardDescription>Owning the NFT lets you watch the entire video.</CardDescription>
          </Card>
          <Card
            onMouseEnter={() => setCard3Hover(true)}
            onMouseLeave={() => setCard3Hover(false)}
            style={card3HoverState}
            onClick={() =>
              window.open('https://glass.helpkit.so/sell/ovEd2mXPBQgQk7SRkYJHDv', '_blank')
            }
          >
            <CardNumber>{`3`}</CardNumber>
            <CardTitle>Sell</CardTitle>
            <CardDescription>{`Done watching the video? Name your price.`}</CardDescription>
          </Card>
        </CardsDiv>
        <CreateDiv>
          <CreateCard>
            <CreateTitle>{`Have a video\nworth watching?`}</CreateTitle>
            <SubmitVideoButton />
          </CreateCard>
          <VideoCard src={`https://glassimg.imgix.net/video.png?fm=webp&w=400&h=225`} />
        </CreateDiv>
      </ContentDiv>
    </Layout>
  )
}

export default IndexPage

export const getServerSideProps = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<{ initialVideo: VideoFullData | undefined }>> => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=21600')

  const result: ApolloQueryResult<Query_Root> = await indexerClient.query({
    query: FIRST_VIDEO_QUERY,
  })

  let initialVideo: VideoFullData | undefined

  const firstMint = result.data?.videoLeaderboard?.[0]?.mintObject
  if (firstMint) {
    const marketplaceData = getMarketplaceData(firstMint)
    const video = await fbEnrichVideoFromId(firstMint.id)
    if (video) {
      const creator = (await fbEnrichUsersFromIds([video.created_by]))[0]
      initialVideo = {
        video,
        creator,
        ...marketplaceData,
      }
    }
  }

  return {
    props: {
      initialVideo,
    },
  }
}

//* =========================== Styled Components ==============================

const ContentDiv = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 85vh;
  @media (min-width: 43em) {
    margin-top: 100px;
  }
`

const ActionsDiv = styled.div<{ top?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  margin-top: ${(props) => (props.top ? '5vh' : '0')};

  @media (min-width: 62em) {
    margin-top: 0;
    align-items: flex-start;
  }
`

const StyledImg = styled.img`
  height: 35vh;
  width: 35vh;
  margin-top: 2vh;
  margin-bottom: -10vh;

  @media (min-width: 43em) {
    margin-top: 5vh;
    margin-bottom: 5vh;
    height: 40vh;
    width: 40vh;
    margin-left: -16vh;
    margin-right: 25px;
  }

  @media (min-width: 62em) {
    height: 67.5vh;
    width: 67.5vh;
    margin-left: -27.5vh;
    margin-right: 5vw;
  }

  @media (min-width: 82em) {
    height: 75vh;
    width: 75vh;
    margin-left: -35vh;
    margin-right: 10vw;
  }
`

const SecondLandingDiv = styled.div`
  margin-top: 25px;
  display: flex;

  align-items: center;
  justify-content: center;

  flex-direction: column-reverse;

  width: 90%;
  min-height: 40vh;

  @media (min-width: 43em) {
    margin-top: 0;
    justify-content: space-between;

    flex-direction: row;
    min-height: 55vh;
    width: 90%;
  }

  @media (min-width: 62em) {
    width: 90%;
    min-height: 75vh;
    flex-direction: row;
  }

  @media (min-width: 82em) {
    width: 85%;
    min-height: 75vh;
    flex-direction: row;
  }
`

const LandingTitle = styled.div`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;

  @media (min-width: 43em) {
    font-size: 42px;
  }

  @media (min-width: 62em) {
    font-size: 50px;
  }

  @media (min-width: 82em) {
    font-size: 62px;
  }
`

const CardsDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 95%;
  gap: 25px;

  @media (min-width: 43em) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 95%;
    margin-top: -125px;
  }

  @media (min-width: 62em) {
    width: 92%;
    margin-top: -150px;
    max-width: 1600px;
  }

  // large desktop
  @media (min-width: 82em) {
    width: 84%;
    max-width: 1800px;
  }
`

const Card = styled(animated.div)`
  background-color: rgba(16, 16, 16, 0.25);
  border-style: solid;
  border-width: 1px;
  border-color: #323232;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-radius: 10px;
  width: 85%;
  height: 100%;
  padding: 20px;
  position: relative;
  z-index: 2;
  cursor: pointer;

  @media (min-width: 43em) {
    width: 27%;
    padding: 20px;
    width: 25%;
  }

  @media (min-width: 62em) {
    padding: 25px;
    width: 25%;
  }

  @media (min-width: 82em) {
    padding: 35px;
    width: 25%;
  }
`

const CardNumber = styled.div`
  color: white;
  position: relative;
  z-index: 1;
  font-size: 30px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: 50px;
  height: 50px;

  @media (min-width: 43em) {
    font-size: 30px;
    width: 50px;
    height: 50px;
  }

  @media (min-width: 62em) {
    font-size: 32px;
    width: 60px;
    height: 60px;
  }

  @media (min-width: 82em) {
    font-size: 36px;
    width: 60px;
    height: 60px;
  }

  :before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 60px;
    padding: 3px;
    background: linear-gradient(94.59deg, #b2d1db -0.06%, #e2b3d6 104.63%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    border-radius: 50%;
  }
`

const CardTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 10px;
  margin-bottom: 10px;

  @media (min-width: 43em) {
    margin-top: 20px;
    margin-bottom: 20px;
    font-size: 30px;
  }

  @media (min-width: 62em) {
    font-size: 32px;
  }

  @media (min-width: 82em) {
    font-size: 32px;
  }
`

const CardDescription = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #cdcdcd;
  text-align: center;

  @media (min-width: 43em) {
    font-size: 14px;
  }

  @media (min-width: 62em) {
    font-size: 16px;
  }

  @media (min-width: 82em) {
    font-size: 20px;
  }
`

const CreateDiv = styled.div`
  /* &:before {
    content: '';
    z-index: -2;
    position: absolute;
    top: 0;
    right: 150px;
    bottom: -100px;
    left: 150px;
    border-radius: 50%;
    background: linear-gradient(108.86deg, #c2defb 0%, #e6c9fa 100%);
    filter: blur(50px);
    transform: translateY(-75px);
    opacity: 0.3;
  }
  &:after {
    content: '';
    z-index: -1;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 30px;
  } */

  position: relative;
  z-index: 0;

  display: flex;
  flex-direction: column-reverse;
  width: 90%;
  align-items: center;
  justify-content: center;
  margin-top: 5vh;
  margin-bottom: 5vh;

  @media (min-width: 43em) {
    flex-direction: row;
    width: 95%;
  }

  @media (min-width: 62em) {
    margin-top: 75px;
    margin-bottom: 10vh;
    width: 90%;
    max-width: 1600px;
  }
`

const CreateCard = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(94.59deg, #b2d1db -0.06%, #e2b3d6 104.63%);
  border-radius: 15px;
  padding: 35px;
  padding-top: 115px;
  width: 70%;

  @media (min-width: 43em) {
    border-radius: 30px;
    padding: 35px;
    width: 60%;
  }

  @media (min-width: 62em) {
    padding: 50px;
    width: 60%;
  }

  @media (min-width: 82em) {
    width: 50%;
  }
`

const CreateTitle = styled.div`
  font-size: 28px;
  font-weight: 700;
  background: -webkit-linear-gradient(0deg, #000, #000, #000, #313131);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0px 4px 15px rgba(0, 0, 0, 0.25);
  line-height: 1.2;
  white-space: pre-wrap;
  margin-bottom: 10px;

  @media (min-width: 43em) {
    margin-bottom: 25px;
    font-size: 42px;
  }

  @media (min-width: 62em) {
    font-size: 54px;
  }

  @media (min-width: 82em) {
    font-size: 60px;
  }
`

const VideoCard = styled.img`
  object-fit: contain;
  width: 75%;
  height: 100%;
  position: relative;
  z-index: 1;
  margin-bottom: -100px;
  /* box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.25); */
  /* border-radius: 15px; */

  @media (min-width: 43em) {
    margin-bottom: 0px;
    width: 35%;
    margin-left: -100px;
  }

  @media (min-width: 62em) {
    width: 35%;
    margin-left: -125px;
  }

  @media (min-width: 82em) {
    width: 35%;
    margin-left: -125px;
  }
`
